<template>
  <DetailViewShell :dv="dv">
    <template #action-buttons>
      <template v-if="!dv.isEditing && isWorkflow">
        <template v-if="canAddVersion">
          <router-link
            v-if="isRemoteWorkflow"
            class="cq-hide-narrow"
            :to="{
              name: 'workflowImport',
              params: { modelType: 'default' },
              query: { workflowId: dv.recordId },
            }"
          >
            <BaseButton
              :disabled="importedFromROCrate"
              :icon="['fa-regular', 'fa-plus']"
              size="small"
              variant="primary"
              :outlined="true"
              label="New Version"
            />
          </router-link>
          <template v-else-if="importedFromROCrate">
            <router-link
              class="cq-hide-narrow"
              :to="{
                name: 'workflowImportROCrate',
                params: { modelType: 'default' },
                query: { workflow_id: id },
              }"
            >
              <BaseButton
                :icon="['fa-regular', 'fa-plus']"
                size="small"
                variant="primary"
                :outlined="true"
                label="New Version"
                tooltip="Import a new version from WorkflowHub"
              />
            </router-link>
          </template>
          <template v-else>
            <BaseButton
              class="cq-hide-narrow"
              :icon="['fa-regular', 'fa-plus']"
              size="small"
              variant="primary"
              :outlined="true"
              :loading="isCreatingWorkflowVersion"
              label="New Version"
              :tooltip="
                !isLatestReleased
                  ? `Release ${latestVersionName} first`
                  : undefined
              "
              @click="createWorkflowVersion"
            />
          </template>
        </template>
        <router-link
          v-if="latestVersionId"
          class="cq-hide-narrow"
          :to="{
            name: 'workflowVersionDetail',
            params: { modelType: 'default', id: latestVersionId },
          }"
        >
          <BaseButton
            :icon="['fa-solid', 'fa-arrow-up-right-from-square']"
            size="small"
            variant="primary"
            :outlined="true"
            label="Go to latest version"
          />
        </router-link>
      </template>
    </template>

    <template #overflow-menu="{ isNarrow }">
      <div v-if="isNarrow && !dv.isEditing && isWorkflow" class="dd-section">
        <div class="dd-section-label">
          <span class="icon"><i class="fa-solid fa-ellipsis-h" /></span>
          Actions
        </div>
        <a
          v-if="canAddVersion"
          class="dd-item"
          :class="{ 'is-disabled': isCreatingWorkflowVersion }"
          @click="createWorkflowVersion"
        >
          <span class="icon"><i class="fa-regular fa-plus" /></span>
          New Version
        </a>
        <a
          v-if="latestVersionId"
          class="dd-item"
          @click="
            $router.push({
              name: 'workflowVersionDetail',
              params: { modelType: 'default', id: latestVersionId },
            })
          "
        >
          <span class="icon">
            <i class="fa-solid fa-arrow-up-right-from-square" />
          </span>
          Go to latest version
        </a>
      </div>
    </template>
    <template #default>
      <DetailViewLayout :dv="dv">
        <template #panel-header>
          <span class="icon">
            <i class="fa-solid fa-barcode" />
          </span>
          <span>Item Details</span>
        </template>
        <template v-if="!dv.isEditing" #panel-header-controls>
          <BadgeTag
            v-if="isRemoteWorkflow"
            icon="fa-brands fa-git-alt"
            label="Remote"
            color="primary"
            :href="remoteRepositoryUrl"
          />
          <StatusWidget
            v-if="dv.recordStatus"
            :status="dv.recordStatus"
            :field-meta="dv.modelAttributes[dv.recordStatusKey]"
          />
        </template>
        <template #panel-content>
          <RecordFieldSections :dv="dv" />
        </template>
        <template #extra-panels>
          <template v-if="isWorkflow">
            <DefaultPanel>
              <template #header>Registered Versions</template>
              <ListComponent
                class="injected-list edge-to-edge"
                app-name="workflows"
                model-name="workflowversions"
                model-type="default"
                vuex-prefix="stockdetail-"
                :is-compact="true"
                :show-add-controls="false"
                :show-global-search="true"
                :show-owner-controls="true"
                :can-save-settings="false"
                :init-context="{ key: 'workflow_id', id: dv.recordId }"
                init-ownership="all"
                :column-names="['name', 'id', 'workflow']"
                :show-deleted="dv.isDeleted"
              />
            </DefaultPanel>
            <DefaultPanel>
              <template #header>Latest files</template>
              <ListComponent
                class="injected-list edge-to-edge"
                app-name="workflows"
                model-name="workflowfiles"
                model-type="default"
                vuex-prefix="stockdetail-"
                :is-compact="true"
                :show-add-controls="false"
                :show-global-search="true"
                :show-owner-controls="true"
                :can-save-settings="false"
                :init-context="{
                  key: 'workflowversion_id',
                  id: latestVersionId,
                }"
                init-ownership="all"
                :column-names="['data_type', 'name', 'workflowversion']"
                :show-deleted="dv.isDeleted"
              >
                <template #more-headers-columns>
                  <th class="cell-shrink cell-centered" />
                </template>
                <template #more-data-columns="{ record: fileRecord }">
                  <td class="cell-shrink cell-centered">
                    <WorkflowFilePreview :file="fileRecord" />
                  </td>
                </template>
              </ListComponent>
            </DefaultPanel>
          </template>

          <DefaultPanel v-if="isWorkflowRun" :key="`datasets-${dv.recordId}`">
            <template #header>Datasets</template>
            <ListComponent
              class="injected-list edge-to-edge"
              app-name="data_management"
              model-name="datasets"
              model-type="default"
              vuex-prefix="wfdetail-"
              :is-compact="true"
              :show-add-controls="false"
              :show-global-search="true"
              :show-owner-controls="true"
              :can-save-settings="false"
              :init-context="{
                key: 'workflowrun_id',
                id: dv.recordId,
              }"
              init-ownership="all"
              :show-deleted="dv.isDeleted"
            />
          </DefaultPanel>
        </template>
      </DetailViewLayout>
    </template>
  </DetailViewShell>
</template>

<script>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/store'
import { post } from '@/api'
import { dateTimeNow } from '@/utils'
import { useDetailView } from '@/composables/useDetailView'

import DefaultPanel from '@/components/DefaultPanel.vue'
import DetailViewLayout from '@/layouts/DetailViewLayout.vue'
import DetailViewShell from '@/components/DetailViewShell.vue'
import RecordFieldSections from '@/components/detail/shared/RecordFieldSections.vue'
import ListComponent from '@/components/listings/ListComponent.vue'
import StatusWidget from '@/components/StatusWidget.vue'
import WorkflowFilePreview from '@/components/WorkflowFilePreview.vue'

export default {
  name: 'WorkflowDetailView',
  components: {
    DefaultPanel,
    DetailViewLayout,
    DetailViewShell,
    RecordFieldSections,
    ListComponent,
    StatusWidget,
    WorkflowFilePreview,
  },
  props: {
    appName: { type: String },
    modelName: { type: String },
    modelType: { type: String },
    id: { type: String },
  },
  setup(props) {
    const store = useStore()
    const router = useRouter()

    const dv = useDetailView({
      appName: props.appName,
      modelName: props.modelName,
      modelType: props.modelType,
      id: props.id,
    })

    // Workflow-specific computed helpers
    const isWorkflow = computed(() => props.modelName === 'workflows')
    const isWorkflowRun = computed(() => props.modelName === 'workflowruns')
    const isRemoteWorkflow = computed(
      () => dv.record?.repository_is_remote?.value,
    )
    const remoteRepositoryUrl = computed(
      () => dv.record?.remote_repository?.value || '',
    )
    const importedFromROCrate = computed(
      () => dv.record?.imported_from_rocrate?.value,
    )

    const workflowVersions = computed(
      () => dv.record?.workflowversions?.value || [],
    )
    const latestVersion = computed(
      () => workflowVersions.value.find(({ is_latest }) => is_latest) || null,
    )
    const latestVersionId = computed(() => latestVersion.value?.id || null)
    const latestVersionName = computed(() => latestVersion.value?.name || '')
    const isLatestReleased = computed(
      () => latestVersion.value?.commit_hash !== '',
    )
    const canAddVersion = computed(
      () =>
        !latestVersion.value ||
        latestVersion.value.deleted ||
        isLatestReleased.value,
    )

    const isCreatingWorkflowVersion = ref(false)
    const createWorkflowVersion = async () => {
      const newWfBaseUrl = 'workflows/workflowversions/'
      const payload = {
        name: { value: { name: `Version ${dateTimeNow()}` } },
        workflow: { value: { id: dv.recordId } },
      }
      try {
        isCreatingWorkflowVersion.value = true
        const response = await post(newWfBaseUrl, {
          data: { results: payload },
        })
        if (response.status === 201) {
          const id = response.data?.results?.id?.value
          if (id)
            router.push({
              name: 'workflowVersionDetail',
              params: {
                appName: 'workflows',
                modelName: 'workflowversions',
                modelType: 'default',
                id,
              },
              query: { edit: true },
            })
        } else {
          throw new Error('Could not create version')
        }
      } catch (error) {
        store.commit('notify', {
          type: 'error',
          message:
            error?.response?.data?.message ||
            error?.message ||
            'Could not create version',
          isDismissable: true,
        })
      } finally {
        isCreatingWorkflowVersion.value = false
      }
    }

    return {
      dv,
      isWorkflow,
      isWorkflowRun,
      isRemoteWorkflow,
      remoteRepositoryUrl,
      importedFromROCrate,
      workflowVersions,
      latestVersion,
      latestVersionId,
      isLatestReleased,
      canAddVersion,
      latestVersionName,
      isCreatingWorkflowVersion,
      createWorkflowVersion,
    }
  },
}
</script>
